import React, { useEffect, useState } from "react";
import { Col, Row } from "antd";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import moment from "moment";

import { Seo } from "../../../api";
import { renderMeta } from "../../../assets/scripts";
import { urlCleaner } from "../../../utils/url-cleaner";
import { renderMetaPixel } from "../../../utils/meta-pixels";
import { ArrowRight } from "../../../assets/icons";
import HeaderPages from "../../../components/Header-Pages/HeaderPages";

import documentsData from "../../../vr_db/documents.json";

import {
  Badge,
  Block,
  DocRow,
  DocumentsSection,
  HeroCard,
  SectionBottom,
} from "./style";

const DocIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M14 2v6h6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>
);

const formatFileBadge = (item, t) => {
  if (!item.fileFormat) return null;
  const size =
    item.fileSizeMb != null
      ? ` · ${item.fileSizeMb.toString().replace(".", ",")} ${t("documents.size.unit")}`
      : "";
  return `${item.fileFormat}${size}`;
};

const formatDate = (iso) => (iso ? moment(iso).format("DD.MM.YYYY") : "");

const docLinkProps = (item) =>
  item.fileUrl
    ? { as: "a", href: item.fileUrl, target: "_blank", rel: "noopener noreferrer" }
    : {};

const pluralizeDocs = (count, t) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  let key = "documents.countDoc.many";
  if (mod10 === 1 && mod100 !== 11) key = "documents.countDoc.one";
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) key = "documents.countDoc.few";
  return t(key, { count });
};

export default function Certificate(props) {
  const { t } = useTranslation();
  const { locale } = props;
  const location = useLocation();
  const [seoData, setSeoData] = useState(undefined);

  useEffect(() => {
    Seo.getAll().then((response) => {
      if (response) {
        const foundResponse = response.find(
          (item) => `/${item.url}` === urlCleaner(location.pathname)
        );
        if (foundResponse) setSeoData(foundResponse);
      }
    });
    const head = document.querySelector(".robot");
    if (head) head.remove();
  }, [location.pathname]);

  const data = documentsData;

  const localized = (obj) => (obj && (obj[locale] || obj.ru)) || "";

  const renderLicenses = () =>
    data.licenses.map((item) => (
      <DocRow key={item.id} {...docLinkProps(item)}>
        <div className="doc-icon">
          <DocIcon />
        </div>
        <div className="doc-body">
          <p className="doc-title">{localized(item.title)}</p>
          <p className="doc-meta">
            {localized(item.issuer)}
            {item.issuedAt ? ` · ${formatDate(item.issuedAt)}` : ""}
          </p>
        </div>
        <div className="doc-aside">
          {formatFileBadge(item, t) && <Badge>{formatFileBadge(item, t)}</Badge>}
        </div>
      </DocRow>
    ));

  return (
    <>
      <Helmet>
        <title>{seoData ? seoData.header[`${locale}`] : t("documents.pageTitle")}</title>
        <meta name="description" content={seoData ? seoData.description[`${locale}`] : ""} />
        <meta name="keywords" content={seoData ? seoData.keyWords[`${locale}`] : ""} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={renderMeta("/certificate", locale, "title")} />
        <meta
          property="og:description"
          content={renderMeta("/certificate", locale, "og:description")}
        />
        <meta property="og:url" content={renderMeta("/certificate", locale, "og:url")} />
        <meta property="og:type" content={renderMeta("/certificate", locale, "og:type")} />
        <meta
          property="og:site_name"
          content={renderMeta("/certificate", locale, "og:site_name")}
        />
        <meta property="og:image" content={renderMeta("/certificate", locale, "og:image")} />
        {renderMetaPixel()}
      </Helmet>

      <HeaderPages {...{ title: t("documents.pageTitle") }} />

      <DocumentsSection>
        <div className="doc-container wide">
          <HeroCard>
            <div className="hero-text">
              <h2 className="hero-title">{t("documents.heroTitle")}</h2>
              <p className="hero-subtitle">{t("documents.heroSubtitle")}</p>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-num">{data.licenses.length}</div>
                <div className="stat-label">{t("documents.stat.licenses")}</div>
              </div>
              <div className="stat">
                <div className="stat-num">{data.agreements.length}</div>
                <div className="stat-label">{t("documents.stat.contracts")}</div>
              </div>
              <div className="stat">
                <div className="stat-num">{data.policies.length}</div>
                <div className="stat-label">{t("documents.stat.policies")}</div>
              </div>
              <div className="stat">
                <div className="stat-num">{data.certificates.length}</div>
                <div className="stat-label">{t("documents.stat.certificate")}</div>
              </div>
            </div>
          </HeroCard>

          <Block>
            <div className="block-head">
              <span className="swatch blue" />
              <h3 className="block-title">{t("documents.section.licenses")}</h3>
              <span className="block-count">{pluralizeDocs(data.licenses.length, t)}</span>
            </div>
            {renderLicenses()}
          </Block>

          <Block>
            <div className="block-head">
              <span className="swatch green" />
              <h3 className="block-title">{t("documents.section.operators")}</h3>
            </div>
            {data.operatorsRegistryFile && (
              <DocRow {...docLinkProps(data.operatorsRegistryFile)}>
                <div className="doc-icon" style={{ background: "#d4f1e0", color: "#1f8a4a" }}>
                  <DocIcon />
                </div>
                <div className="doc-body">
                  <p className="doc-title">{t("documents.operators.downloadCta")}</p>
                </div>
                <div className="doc-aside">
                  {formatFileBadge(data.operatorsRegistryFile, t) && (
                    <Badge>{formatFileBadge(data.operatorsRegistryFile, t)}</Badge>
                  )}
                </div>
              </DocRow>
            )}
          </Block>

          <Block>
            <div className="block-head">
              <span className="swatch olive" />
              <h3 className="block-title">{t("documents.section.agreements")}</h3>
              <span className="block-count">{pluralizeDocs(data.agreements.length, t)}</span>
            </div>
            {data.agreements.map((item) => (
              <DocRow key={item.id} {...docLinkProps(item)}>
                <div className="doc-icon">
                  <DocIcon />
                </div>
                <div className="doc-body">
                  <p className="doc-title">{localized(item.title)}</p>
                </div>
                <div className="doc-aside">
                  {formatFileBadge(item, t) && <Badge>{formatFileBadge(item, t)}</Badge>}
                </div>
              </DocRow>
            ))}
          </Block>

          <Block>
            <div className="block-head">
              <span className="swatch teal" />
              <h3 className="block-title">{t("documents.section.policies")}</h3>
              <span className="block-count">{pluralizeDocs(data.policies.length, t)}</span>
            </div>
            {data.policies.map((item) => (
              <DocRow key={item.id} {...docLinkProps(item)}>
                <div className="doc-icon" style={{ background: "#d4ecef", color: "#1e6b7a" }}>
                  <DocIcon />
                </div>
                <div className="doc-body">
                  <p className="doc-title">{localized(item.title)}</p>
                </div>
                <div className="doc-aside">
                  {formatFileBadge(item, t) && <Badge>{formatFileBadge(item, t)}</Badge>}
                </div>
              </DocRow>
            ))}
          </Block>

          <Block>
            <div className="block-head">
              <span className="swatch peach" />
              <h3 className="block-title">{t("documents.section.certificates")}</h3>
              <span className="block-count">{pluralizeDocs(data.certificates.length, t)}</span>
            </div>
            {data.certificates.map((item) => (
              <DocRow key={item.id} {...docLinkProps(item)}>
                <div className="doc-icon" style={{ background: "#fde3cd", color: "#c0691b" }}>
                  <DocIcon />
                </div>
                <div className="doc-body">
                  <p className="doc-title">{localized(item.title)}</p>
                  <p className="doc-meta">
                    {t("documents.issuedBy", { by: localized(item.issuer) })}
                    {item.fileFormat ? ` · ${formatFileBadge(item, t)}` : ""}
                  </p>
                </div>
                <div className="doc-aside">
                  {item.validUntil && (
                    <Badge className="valid">
                      {t("documents.validUntil", { date: formatDate(item.validUntil) })}
                    </Badge>
                  )}
                </div>
              </DocRow>
            ))}
          </Block>

          <Block>
            <div className="block-head">
              <span className="swatch lavender" />
              <h3 className="block-title">{t("documents.section.constituent")}</h3>
              <span className="block-count">{pluralizeDocs(data.constituent.length, t)}</span>
            </div>
            {data.constituent.map((item) => (
              <DocRow key={item.id} {...docLinkProps(item)}>
                <div className="doc-icon" style={{ background: "#e6e1f5", color: "#5b4ba7" }}>
                  <DocIcon />
                </div>
                <div className="doc-body">
                  <p className="doc-title">{localized(item.title)}</p>
                </div>
                <div className="doc-aside">
                  {formatFileBadge(item, t) && <Badge>{formatFileBadge(item, t)}</Badge>}
                </div>
              </DocRow>
            ))}
          </Block>
        </div>
      </DocumentsSection>

      <SectionBottom>
        <div className="container">
          <Row gutter={12}>
            <Col lg={12} span={24}>
              <div className="wrap-block">
                <div className="text-block">
                  <h4>{t("Main.text10")}</h4>
                  <p>{t("Main.text11")}</p>
                </div>
                <Link to={`${locale === "ru" ? "/" : `/${locale}/`}experience`}>
                  <ArrowRight />
                </Link>
              </div>
            </Col>
            <Col lg={12} span={24}>
              <div className="wrap-block">
                <div className="text-block">
                  <h4>{t("Main.text12")}</h4>
                  <p>{t("Main.text13")}</p>
                </div>
                <Link to={`${locale === "ru" ? "/" : `/${locale}/`}networking`}>
                  <ArrowRight />
                </Link>
              </div>
            </Col>
          </Row>
        </div>
      </SectionBottom>
    </>
  );
}
